import type { TaskStatus, Priority } from '../../types';
import type { TaskFiltersParams } from '../../types/api/requests/tasks';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import { effectiveTaskStatuses } from '../../constants/taskStatuses';

// ===== SORT FIELD TYPE =====

export type TaskSortField =
  | 'title'
  | 'status'
  | 'priority'
  | 'dueDate'
  | 'updatedAt'
  | 'createdAt'
  | 'assignee'
  | 'location'
  | 'progress';

// ===== SLICE INTERFACE =====

export interface TaskFiltersSlice {
  taskFilters: {
    search: string;
    status: TaskStatus[];
    priority: Priority[];
    assignedToId: string[];
    dateFrom: Date | null;
    dateTo: Date | null;
    location: string;
    page: number;
    pageSize: number;
    sortBy: TaskSortField;
    sortDescending: boolean;

    // Actions
    setSearch: (search: string) => void;
    setStatus: (status: TaskStatus[]) => void;
    setPriority: (priority: Priority[]) => void;
    setAssignedToId: (assignedToId: string[]) => void;
    setDateRange: (from: Date | null, to: Date | null) => void;
    setLocation: (location: string) => void;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    setSortBy: (sortBy: TaskSortField) => void;
    setSortDescending: (sortDescending: boolean) => void;
    resetFilters: () => void;
  };
}

// ===== SLICE CREATOR =====

export const createTaskFiltersSlice = (
  set: (fn: (state: TaskFiltersSlice) => void) => void,
  _get: () => unknown,
  _store: unknown
) => ({
  taskFilters: {
    search: '',
    status: [] as TaskStatus[],
    priority: [] as Priority[],
    assignedToId: [] as string[],
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
    location: '',
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: 'createdAt' as TaskSortField,
    sortDescending: true,

    setSearch: (search: string) => {
      set((state) => {
        state.taskFilters.search = search;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    setStatus: (status: TaskStatus[]) => {
      set((state) => {
        state.taskFilters.status = status;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    setPriority: (priority: Priority[]) => {
      set((state) => {
        state.taskFilters.priority = priority;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    setAssignedToId: (assignedToId: string[]) => {
      set((state) => {
        state.taskFilters.assignedToId = assignedToId;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    setDateRange: (from: Date | null, to: Date | null) => {
      set((state) => {
        state.taskFilters.dateFrom = from;
        state.taskFilters.dateTo = to;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    setLocation: (location: string) => {
      set((state) => {
        state.taskFilters.location = location;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    setPage: (page: number) => {
      set((state) => {
        state.taskFilters.page = page;
      });
    },

    setPageSize: (pageSize: number) => {
      set((state) => {
        state.taskFilters.pageSize = pageSize;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    setSortBy: (sortBy: TaskSortField) => {
      set((state) => {
        state.taskFilters.sortBy = sortBy;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    setSortDescending: (sortDescending: boolean) => {
      set((state) => {
        state.taskFilters.sortDescending = sortDescending;
        state.taskFilters.page = DEFAULT_PAGE;
      });
    },

    resetFilters: () => {
      set((state) => {
        state.taskFilters.search = '';
        state.taskFilters.status = [];
        state.taskFilters.priority = [];
        state.taskFilters.assignedToId = [];
        state.taskFilters.dateFrom = null;
        state.taskFilters.dateTo = null;
        state.taskFilters.location = '';
        state.taskFilters.page = DEFAULT_PAGE;
        state.taskFilters.pageSize = DEFAULT_PAGE_SIZE;
        state.taskFilters.sortBy = 'createdAt';
        state.taskFilters.sortDescending = true;
      });
    },
  },
});

// ===== API PARAMS BUILDER =====

/**
 * Convert task filter slice state to API request params.
 * Maps slice field names to the backend TaskFiltersParams contract.
 */
export function buildTaskFiltersParams(
  filters: Pick<
    TaskFiltersSlice['taskFilters'],
    | 'search'
    | 'status'
    | 'priority'
    | 'assignedToId'
    | 'dateFrom'
    | 'dateTo'
    | 'location'
    | 'page'
    | 'pageSize'
    | 'sortBy'
    | 'sortDescending'
  >
): TaskFiltersParams {
  const params: TaskFiltersParams = {};

  if (filters.search) params.search = filters.search;
  if (filters.location) params.location = filters.location;
  // Empty status selection => send all default-visible statuses (everything
  // except Archived) so archived tasks stay hidden until explicitly selected.
  params.statuses = effectiveTaskStatuses(filters.status).join(',');
  if (filters.priority.length > 0)
    params.priorities = filters.priority.join(',');
  if (filters.assignedToId.length > 0)
    params.assignedToIds = filters.assignedToId.join(',');
  if (filters.dateFrom)
    params.dueDateFrom = filters.dateFrom.toISOString().split('T')[0];
  if (filters.dateTo)
    params.dueDateTo = filters.dateTo.toISOString().split('T')[0];
  if (filters.sortBy) params.sortBy = filters.sortBy;
  params.sortDescending = filters.sortDescending;
  params.pageSize = filters.pageSize;

  return params;
}
